import { Injectable } from '@angular/core';
import { ApiService } from './api';

import 'rxjs/add/operator/map';
import { Account } from '../model/account';

@Injectable()
export class DataService {
    constructor(private api: ApiService) { }

    authenticate(email: string, password: string) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithoutAuth(this.api.endpoints.authenticate + '/token', {
                    EmailAddress: email,
                    Password: password,
                })
                .then(data => resolve(data), error => reject(error));
        });
    }

    logout() {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.authenticate + '/revokeToken')
                .then(data => resolve(data), error => reject(error));
        });
    }

    refreshUserData() {
        return new Promise((resolve, reject) => {
            this.api.getWithAuth(this.api.endpoints.authenticate + '/refreshAuthResponse')
            .then(data => resolve(data), error => reject(error));
        });
    }

    updateUserPassword(id, password) {
        return new Promise((resolve, reject) => {
            this.api
                .putWithAuth(
                this.api.endpoints.users +
                '/updatePassword?id=' +
                id +
                '&password=' +
                password
                )
                .then(data => resolve(data), error => reject(error));
        });
    }

    sendTemporaryPassword(emailAddress: string) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithoutAuth(this.api.endpoints.users + '/sendTemporaryPassword?emailAddress=' + emailAddress)
                .then(data => resolve(data), error => reject(error));
        });
    }

    validateResetCode(resetCode: string) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithoutAuth(this.api.endpoints.users + '/validateResetCode?resetCode=' + resetCode)
                .then(data => resolve(data), error => reject(error));
        });
    }

    setNewPassword(emailAddress: string, resetCode: string, password: string) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithoutAuth(this.api.endpoints.users + '/setNewPassword?emailAddress=' + emailAddress + '&resetCode=' + resetCode + '&newPassword=' + password)
                .then(data => resolve(data), error => reject(error));
        });
    }

    /* Timers ==================== */

    getTimer(key: string) {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(this.api.endpoints.timer + '/get?id=' + key)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getRecentTimers() {
        return this.api.getWithAuth(this.api.endpoints.timer + '/getRecentTimers');
    }

    
    getStaffPageSettings() {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(this.api.endpoints.manage + '/getStaffPageSettings')
                .then(data => resolve(data), error => reject(error));
        });
    }
    
    getStaffMembers(organizationId, producerId) {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(this.api.endpoints.manage + '/getStaffMembers?organizationId=' + organizationId + '&producerId=' + producerId)
                .then(data => resolve(data), error => reject(error));
        });
    }

    /* Search ==================== */
    async getUserSearch(firstName: string = "", lastName: string = "", membershipId: string = "") {
        let userResults: Account[] = [];

        try {
            const resp: any = await this.api.getWithAuth(`${this.api.endpoints.search}/searchForAccounts?firstName=${firstName}&lastName=${lastName}&membershipId=${membershipId}`);
            userResults = resp ? resp.map(x => Account.create(x)) : [];
        } catch(err) {
            console.error('UserService - getUserSearch', err);
            return [];
        }

        return userResults;
    }

    async getStaffUserSearch(firstName: string = "", lastName: string = "", membershipId: string = "") {
        let userResults: Account[] = [];

        try {
            const resp: any = await this.api.getWithAuth(`${this.api.endpoints.search}/staffSearchForAccounts?firstName=${firstName}&lastName=${lastName}&membershipId=${membershipId}`);
            userResults = resp ? resp.map(x => Account.create(x)) : [];
        } catch(err) {
            console.error('UserService - getUserSearch', err);
            return [];
        }

        return userResults;
    }

    getEmailSearch(query: string) {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(this.api.endpoints.search + '/accountsByEmail?query=' + query)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getStaffSearch(query: string) {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(this.api.endpoints.search + '/staff?query=' + query)
                .then(data => resolve(data), error => reject(error));
        });
    }

	/* Notifications ==================== */

	registerUserForNotifications(notificationData: any) {
		return this.api.postWithAuth(this.api.endpoints.notifications).then(registrationId => {
			return this.api.postWithAuth(this.api.endpoints.notifications + '/' + registrationId)
		})
	}

    getEventRegistrationsForUserByDivision(data: any) {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(
                this.api.endpoints.eventRegistrations +
                '/getAllForUserByDivision?divisionId=' +
                data['eventDivisionId'] +
                '&roperId=' +
                data['roperId']
                )
                .then(data => resolve(data), error => reject(error));
        });
    }

    getEventRunsForUser(data: any) {
        return this.api.getWithAuth(this.api.endpoints.eventRuns + `/getallforuser?divisionId=${data.divisionId}&roperId=${data.roperId}`)
    }

    getEventRunsByDivisionId(key: number) {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(
                this.api.endpoints.eventRuns + '/getAll?eventDivisionId=' + key
                )
                .then(data => resolve(data), error => reject(error));
        });
    }

    updateEventRun(eventDivisionId: number, data: any, buyback: boolean) {
        return new Promise((resolve, reject) => {
            this.api
                .putWithAuth(
                this.api.endpoints.eventRuns +
                '/update?eventDivisionId=' + eventDivisionId +
                '&buyback=' + buyback,
                data
                )
                .then(data => resolve(data), error => reject(error));
        });
    }

    payExtraRun(runId) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(
                this.api.endpoints.eventRuns +
                '/payExtraRun?runId=' + runId)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getUpdatedRuns(eventDivisionId: number, currentRounds) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(
                this.api.endpoints.eventRuns +
                '/getUpdatedEventRuns?eventDivisionId=' +
                eventDivisionId,
                currentRounds
                )
                .then(data => resolve(data), error => reject(error));
        });
    }

    getRunsForRound(eventDivisionId: number, currentRound: number) {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(
                this.api.endpoints.eventRuns +
                '/getAllForRound?eventDivisionId=' +
                eventDivisionId +
                '&round=' +
                currentRound
                )
                .then(data => resolve(data), error => reject(error));
        });
    }

    clearRunsForEventDivision(eventDivisionId) {
        return new Promise((resolve, reject) => {
            this.api
                .putWithAuth(
                this.api.endpoints.eventDivisions +
                '/clearRuns?eventDivisionId=' +
                eventDivisionId
                )
                .then(data => resolve(data), error => reject(error));
        });
    }

    createEventRun(eventDivisionId, eventRun, headerPaid, heelerPaid) {
        return new Promise((resolve, reject) => {
            this.api
                .putWithAuth(
                this.api.endpoints.eventRuns +
                '/create?eventDivisionId=' +
                eventDivisionId +
                '&headerPaid=' +
                headerPaid +
                '&heelerPaid=' +
                heelerPaid,
                eventRun
                )
                .then(data => resolve(data), error => reject(error));
        });
    }

    deleteEventRun(eventRunId) {
        return new Promise((resolve, reject) => {
            this.api
                .deleteWithAuth(
                this.api.endpoints.eventRuns + '/delete?eventRunId=' + eventRunId
                )
                .then(data => resolve(data), error => reject(error));
        });
    }
}



// WEBPACK FOOTER //
// ./src/services/data.ts